<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Group meetings without the scheduling chaos"
            subtitle="Run workshops, cohort sessions, office hours, and group programs with one clean booking flow, including cases where one organizer needs to reserve multiple seats in a single booking."
            buttonLabel="Create your first group meeting"
            :buttonUrl="heroRegisterUrl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div class="order-1 lg:order-1">
                        <FeaturePageSectionHeader
                            title="Built for paid and free group sessions"
                            subtitle="Configure one group service and let attendees book their own spots or reserve multiple seats in one checkout."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <div class="mt-7 flex justify-start">
                            <FeaturePagePrimaryButton
                                :href="ctaRegisterUrl"
                                label="Launch a group session"
                            />
                        </div>
                    </div>

                    <div class="order-2 lg:order-2">
                        <FeaturePageMockPanel
                            title="City Walking Tour - Saturday Group"
                            badgeText="4 seats reserved"
                            badgeClass="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800"
                        >
                            <FeaturePageInfoCard
                                title="Session details"
                                description="Sat • 16:00 • 90 min"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Reservation"
                                description="1 organizer booking • 4 seats for a family group"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Payment"
                                description="$196 collected at booking ($49 × 4 seats)"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />

                            <FeaturePageInfoCard
                                title="Capacity"
                                description="14/20 seats reserved"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Group meeting capabilities"
                    subtitle="Run paid and free sessions with attendee-level controls, multi-seat booking support, and one clean booking flow."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="groupFeature in groupFeatures"
                        :key="groupFeature.title"
                        :title="groupFeature.title"
                        :description="groupFeature.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                    />
                </div>
            </div>
        </section>

        <section class="bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Group meetings work especially well when one client often books for other people in the same household, friend group, or team."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-2">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import { MAXIMUM_NUMBER_OF_SEATS_PER_MEETING } from '@/Shared/AutoGenerated/Main'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface GroupFeature {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.GroupMeetingsHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.GroupMeetingsCta,
    )

    const groupFeatures: GroupFeature[] = [
        {
            title: 'Schedule group sessions',
            description:
                'Admins can create one service that supports multiple attendees in the same slot.',
        },
        {
            title: 'Reserve multiple seats',
            description:
                'One attendee can reserve several seats in a single booking when they are organizing for a family, couple, or small team.',
        },
        {
            title: 'Set attendee caps',
            description: `Choose the maximum number of seats per session, with a global cap limit of ${MAXIMUM_NUMBER_OF_SEATS_PER_MEETING}.`,
        },
        {
            title: 'Collect payment per attendee',
            description:
                'Charge each participant during booking instead of chasing payments later.',
        },
        {
            title: 'Capture intake forms',
            description:
                'Ask booking questions so each attendee provides the needed context upfront.',
        },
        {
            title: 'Individual spot cancellations',
            description:
                'Attendees can cancel their own spots without canceling the full group session.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Family and friend group tours',
            description:
                'Let one organizer reserve several seats for a city tour, tasting, or local experience without forcing one booking per person.',
        },
        {
            title: 'Couples and family sessions',
            description:
                'Use one booking when a client is scheduling for a partner, parent, teen, or sibling who will attend the same session together.',
        },
        {
            title: 'Small team training',
            description:
                'A manager can book seats for teammates in one checkout for onboarding, advisory sessions, or hands-on workshops.',
        },
        {
            title: 'Parent and student planning calls',
            description:
                'Make it easy for one parent to reserve seats for themselves and the student instead of coordinating separate bookings.',
        },
    ]
</script>
